import {HedgePositionTransaction} from "./data-model/hedge-position-transaction";
import {HedgePositionGroup} from "./data-model/hedge-position-group";
import {findHCF, isValidNumber, isVoid} from "../utils";
import {HedgeOrderGroup} from "./data-model/hedge-order-group";
import {HedgeOrder} from "./data-model/hedge-order";
import * as Enumerable from "linq";

export function getSubTotals(transType: 'current' | 'new', transactions: HedgePositionTransaction[], defaultQty?: number, getNormFactor?: (asset: string) => number): number {

    let selectorFunc: (x: HedgePositionTransaction) => HedgePositionGroup;

    if (transType === 'current') {
        selectorFunc = (x) => x.beforeState;
    } else if (transType === 'new') {
        selectorFunc = (x) => x.afterState;
    } else {
        console.error('unknown trans type: ' + transType);
        return undefined;
    }

    const posGroup = transactions.map(selectorFunc)
        .filter(x => !isVoid(x))
        .filter(x => !isVoid(x.positions));

    let groupSums = posGroup
        .flatMap(grp => {

            if (isVoid(grp.hcf)) {
                const qtties = grp.positions.map(x => Math.abs(x.qty));
                const hcf = findHCF(qtties);
                grp.hcf = hcf;
            }

            let ratio = 1;

            if (isValidNumber(defaultQty, true)) {
                ratio = grp.hcf / defaultQty;
            }

            let prices = grp.positions.map(pos => {
                if (!isValidNumber(pos.price)) {
                    return undefined;
                }
                const px = pos.price || 0;
                const qx = Math.abs(pos.qty);
                const nf = getNormFactor ? getNormFactor(pos.asset) : 1;
                let result = px * qx * nf;
                return result;
            });

            let sum = null;

            const pricesEnum = Enumerable.from(prices);

            if (pricesEnum.all(x => isValidNumber(x, true))) {
                sum = pricesEnum.sum();
            }

            if (isValidNumber(sum)) {
                sum = sum / grp.hcf;

                if (isValidNumber(ratio, true)) {
                    sum = sum * ratio;
                }
            }

            return sum;
        });

    let totalSum = null;

    if (groupSums.some(gs => isValidNumber(gs, true))) {
        totalSum = Enumerable.from(groupSums).sum();
    }

    return totalSum;

}

export function getGroupTotal(group: HedgePositionGroup, defaultQty?: number, getNormFactor?: (asset: string) => number): number {

    if (isVoid(group) || isVoid(group.positions)) {
        return undefined;
    }

    if (isVoid(group.hcf)) {
        const qtties = group.positions.map(x => Math.abs(x.qty));
        const hcf = findHCF(qtties);
        group.hcf = hcf;
    }

    let sums = group.positions
        .map(x => {
            if (!isValidNumber(x.price, true)) {
                return undefined;
            }
            const px = x.price;
            const qx = Math.abs(x.qty);
            const nf = getNormFactor ? getNormFactor(x.asset) : 1;
            let result = px * qx * nf;
            return result;
        });

    let totalSum = null;

    if (sums.every(gs => isValidNumber(gs, true))) {

        totalSum = Enumerable.from(sums).sum();

        totalSum = totalSum / group.hcf;

        if (isValidNumber(defaultQty, true)) {
            const ratio = group.hcf / defaultQty;
            console.assert(isValidNumber(ratio, true), 'ratio is not valid');
            totalSum = totalSum * ratio;
        }
    }

    return totalSum;
}

export function getOrderGroupTotal(orderGroup: HedgeOrderGroup, defaultQty?: number, getNormFactor?: (asset: string) => number) {

    if (isVoid(orderGroup) || isVoid(orderGroup.orders)) {
        return undefined;
    }

    if (isVoid(orderGroup.hcf)) {
        const qtties = orderGroup.orders.map(x => Math.abs(x.qty));
        const hcf = findHCF(qtties);
        orderGroup.hcf = hcf;
    }

    const ratio = orderGroup.hcf / defaultQty;

    let sums = orderGroup.orders
        .map(x => {

            if (!isValidNumber(x.price, true)) {
                return undefined;
            }

            const px = x.price;

            const qx = Math.abs(x.qty);

            const nf = getNormFactor ? getNormFactor(x.asset) : 1;

            let result = px * qx * nf;


            return result;

        });

    let totalSum = null;

    if (sums.every(gs => isValidNumber(gs, true))) {

        totalSum = Enumerable.from(sums).sum();

        totalSum = totalSum / orderGroup.hcf;

        if (isValidNumber(defaultQty, true)) {
            console.assert(isValidNumber(ratio, true), 'ratio is not valid');
            totalSum = totalSum * ratio;
        }
    }

    return totalSum;
}

export function getOrderGroupSubTotal(orders: HedgeOrder[], defaultQty?: number, getNormFactor?: (asset: string) => number) {

    if (isVoid(orders)) {
        return undefined;
    }

    if (isVoid(orders[0].hcf)) {
        const qtties = orders.map(x => Math.abs(x.qty));
        const hcf = findHCF(qtties);
        orders.forEach(o => o.hcf = hcf);
    }

    let sums = orders
        .map(x => {
            if (!isValidNumber(x.price, true)) {
                return undefined;
            }
            const px = x.price || 0;
            const qx = Math.abs(x.qty);
            const nf = getNormFactor ? getNormFactor(x.asset) : 1;
            let result = px * qx * nf;
            return result;
        });

    let totalSum = null;

    if (sums.every(gs => isValidNumber(gs, true))) {
        totalSum = Enumerable.from(sums).sum();

        totalSum = totalSum / orders[0].hcf;

        if (isValidNumber(defaultQty, true)) {
            const ratio = orders[0].hcf / defaultQty;
            console.assert(isValidNumber(ratio, true), 'ratio is not valid');
            totalSum = totalSum * ratio;
        }
    }

    return totalSum;
}